/**
 * Expanded form for a single preset item
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { toClickIdStructure, toDomainScheme } from '@trafficpulse/shared';

import { Checkbox } from '@/components/ui';
import { Input } from '@/components/ui';
import { SearchableSelect } from '@/components/ui';
import {
  ShortlinkOptions,
  StaticTagsPickerSection,
} from '@/features/smswiser-campaigns/components/drop-modals';
import type { EditableItem } from './useEditPresetForm';
import type { PresetsModalCatalog } from '../shared';

export interface EditPresetItemExpandedProps {
  item: EditableItem;
  catalog: PresetsModalCatalog;
  onUpdate: (patch: Partial<EditableItem>) => void;
}

export const EditPresetItemExpanded: FC<EditPresetItemExpandedProps> = ({
  item,
  catalog,
  onUpdate,
}) => {
  const campaignOpts = catalog.campaigns.map((c) => ({ value: c.id, label: c.name }));
  const gatewayOpts = catalog.gateways.map((g) => ({ value: g.id, label: g.name }));
  const leadsOpts = catalog.leadsLists.map((l) => ({ value: l.id, label: l.name }));
  const tldOpts = catalog.tlds.map((t) => ({ value: t.value, label: t.displayName }));

  return (
    <div className="space-y-4 border-t border-border/60 bg-muted/5 p-4">
      <section>
        <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Drop configuration
        </h4>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-2">
            <label className="text-xs font-medium text-foreground">Campaign</label>
            <SearchableSelect
              options={campaignOpts}
              value={item.campaignWiserId ?? 0}
              onChange={(v) => onUpdate({ campaignWiserId: Number(v) })}
              placeholder="Select campaign"
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium text-foreground">Gateway</label>
            <SearchableSelect
              options={gatewayOpts}
              value={item.routeId}
              onChange={(v) => onUpdate({ routeId: Number(v) })}
              placeholder="Select gateway"
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium text-foreground">Leads list</label>
            <SearchableSelect
              options={leadsOpts}
              value={item.leadsListWiserId}
              onChange={(v) => onUpdate({ leadsListWiserId: Number(v) })}
              placeholder="Select leads"
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium text-foreground">TLD</label>
            <SearchableSelect
              options={tldOpts}
              value={item.shortLinkTldValue}
              onChange={(v) => onUpdate({ shortLinkTldValue: String(v) })}
              placeholder="Select TLD"
            />
          </div>
        </div>
      </section>
      <section>
        <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Amount & tagging
        </h4>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
          <div className="w-24 space-y-2">
            <label className="text-xs font-medium text-foreground">Amount</label>
            <Input
              type="number"
              min={1}
              value={item.amount}
              onChange={(e) => onUpdate({ amount: Number(e.target.value) || 1 })}
              className="h-9"
            />
          </div>
          <div className="min-w-[180px] flex-1 space-y-2">
            <label className="text-xs font-medium text-foreground">Static tag</label>
            <StaticTagsPickerSection
              mode="single"
              selectedId={item.staticTagId ?? null}
              onChange={(id) => onUpdate({ staticTagId: id ?? undefined })}
              compact
            />
          </div>
        </div>
      </section>
      <section>
        <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Options
        </h4>
        <div className="flex flex-wrap gap-6">
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox
              checked={item.forceSend ?? false}
              onCheckedChange={(c) => onUpdate({ forceSend: Boolean(c) })}
            />
            <span>Include duplicates</span>
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox
              checked={item.useOnlyNewDomains ?? false}
              onCheckedChange={(c) => onUpdate({ useOnlyNewDomains: Boolean(c) })}
            />
            <span>New domains only</span>
          </label>
        </div>
        <div className="mt-3 max-w-md">
          <ShortlinkOptions
            compact
            clickIdStructure={toClickIdStructure(item.clickIdStructure)}
            setClickIdStructure={(v) => onUpdate({ clickIdStructure: toClickIdStructure(v) })}
            domainScheme={toDomainScheme(item.domainScheme)}
            setDomainScheme={(v) => onUpdate({ domainScheme: toDomainScheme(v) })}
          />
        </div>
      </section>
    </div>
  );
};
